import { useState, useEffect, useMemo } from 'react';
import { api } from '../api/client';
import type { PocketTenant, BillingPocket, BillableMeterV2 } from '../api/types';
import MeterTypeIcon from '../components/MeterTypeIcon';
import { Card, Badge, Alert, DataTable } from '../components/ui';
import type { DataTableColumn } from '../components/ui';

interface BuildingAsset {
  id: string;
  name: string;
}

type StatusFilter = 'active' | 'past' | 'all';

const today = (): string => new Date().toISOString().slice(0, 10);

function isActive(t: PocketTenant): boolean {
  const d = today();
  return t.start_date <= d && (t.end_date === null || t.end_date >= d);
}

export default function Tenants() {
  const [tenants, setTenants] = useState<PocketTenant[]>([]);
  const [pockets, setPockets] = useState<BillingPocket[]>([]);
  const [buildings, setBuildings] = useState<BuildingAsset[]>([]);
  // Billable meters carry per-meter tariff_id/name + assigned_pocket_id, so
  // we derive per-pocket tariff list from them. A pocket can have multiple
  // meters → multiple tariffs (typically one elec + one water).
  const [meters, setMeters] = useState<BillableMeterV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [buildingFilter, setBuildingFilter] = useState('');
  const [pocketFilter, setPocketFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState<PocketTenant | null>(null);
  const [form, setForm] = useState({
    pocket_id: '',
    trading_name: '',
    registered_name: '',
    account_number: '',
    start_date: today(),
    end_date: '',
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => { loadData(); }, [statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [tenantsRes, pocketsRes, buildingsRes, metersRes] = await Promise.all([
        api.getTenants({ include_history: statusFilter !== 'active' }),
        api.getPockets(),
        api.getBuildings(),
        api.getBillableMetersV2(),
      ]);
      setTenants(tenantsRes.data);
      setPockets(pocketsRes.data);
      setBuildings(buildingsRes.data);
      setMeters(metersRes.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tenants.filter(t => {
      if (statusFilter === 'active' && !isActive(t)) return false;
      if (statusFilter === 'past' && isActive(t)) return false;
      if (buildingFilter && t.building_asset_id !== buildingFilter) return false;
      if (pocketFilter && t.pocket_id !== pocketFilter) return false;
      if (q) {
        const hay = `${t.trading_name} ${t.registered_name ?? ''} ${t.account_number ?? ''} ${t.pocket_name ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tenants, search, buildingFilter, pocketFilter, statusFilter]);

  const buildingNameById = useMemo(() => new Map(buildings.map(b => [b.id, b.name])), [buildings]);

  // Map pocket_id → list of meters with a tariff assigned, deduped by tariff_id.
  // We render one chip per distinct tariff in the pocket (rare to be more
  // than 2: typically one electricity + one water).
  const tariffsByPocket = useMemo(() => {
    const map = new Map<string, Array<{ tariff_id: string; tariff_name: string; meter_type: string }>>();
    for (const m of meters) {
      if (!m.assigned_pocket_id || !m.tariff_id) continue;
      const list = map.get(m.assigned_pocket_id) ?? [];
      if (!list.some(x => x.tariff_id === m.tariff_id)) {
        list.push({ tariff_id: m.tariff_id, tariff_name: m.tariff_name, meter_type: m.meter_type });
      }
      map.set(m.assigned_pocket_id, list);
    }
    return map;
  }, [meters]);

  // Trading Name identifies the tenant; Building / Pocket is the field this
  // page is organized around (admins scan/manage by location) — the scanned
  // "second field". Status is a Badge (never primary — see DataTable's own
  // collapsing-branch note: no overflow-x-auto fallback below lg, so a badge
  // that can't wrap can blow the layout out) so it stays secondary along
  // with Account #/Tariff/Start/End. rowActions carries Edit/Delete.
  const tenantColumns: DataTableColumn<PocketTenant>[] = [
    {
      key: 'trading_name',
      label: 'Trading Name',
      priority: 'primary',
      render: (t) => (
        <>
          <div className="font-medium text-content">{t.trading_name}</div>
          {t.registered_name && (
            <div className="text-xs text-content-muted">{t.registered_name}</div>
          )}
        </>
      ),
    },
    {
      key: 'account_number',
      label: 'Account #',
      priority: 'secondary',
      render: (t) => <span className="text-content font-mono">{t.account_number ?? '—'}</span>,
    },
    {
      key: 'building_pocket',
      label: 'Building / Pocket',
      priority: 'primary',
      render: (t) => {
        const buildingName = t.building_asset_id ? buildingNameById.get(t.building_asset_id) : null;
        return (
          <>
            <div className="text-content">{t.pocket_name ?? '—'}</div>
            {buildingName && <div className="text-xs text-content-muted">{buildingName}</div>}
          </>
        );
      },
    },
    {
      key: 'tariff',
      label: 'Tariff',
      priority: 'secondary',
      render: (t) => {
        const list = tariffsByPocket.get(t.pocket_id) ?? [];
        if (list.length === 0) return <span className="text-content-muted italic">none</span>;
        return (
          <div className="space-y-0.5">
            {list.map(x => (
              <div key={x.tariff_id} className="flex items-center gap-1.5">
                <MeterTypeIcon type={x.meter_type} className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="text-content">{x.tariff_name}</span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      key: 'start_date',
      label: 'Start',
      priority: 'secondary',
      render: (t) => <span className="text-content">{t.start_date}</span>,
    },
    {
      key: 'end_date',
      label: 'End',
      priority: 'secondary',
      render: (t) => t.end_date ?? <span className="text-content-muted italic">open</span>,
    },
    {
      key: 'status',
      label: 'Status',
      priority: 'secondary',
      render: (t) => {
        const active = isActive(t);
        return (
          <Badge tone={active ? 'success' : 'neutral'} className="gap-1.5">
            <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-teal-600 dark:bg-teal-300' : 'bg-stone-500 dark:bg-stone-400'}`} />
            {active ? 'Active' : 'Past'}
          </Badge>
        );
      },
    },
  ];

  const openCreate = () => {
    setEditingTenant(null);
    setForm({
      pocket_id: pocketFilter || '',
      trading_name: '',
      registered_name: '',
      account_number: '',
      start_date: today(),
      end_date: '',
    });
    setFormError(null);
    setShowModal(true);
  };

  const openEdit = (t: PocketTenant) => {
    setEditingTenant(t);
    setForm({
      pocket_id: t.pocket_id,
      trading_name: t.trading_name,
      registered_name: t.registered_name ?? '',
      account_number: t.account_number ?? '',
      start_date: t.start_date,
      end_date: t.end_date ?? '',
    });
    setFormError(null);
    setShowModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.pocket_id) { setFormError('Pocket is required'); return; }
    if (!form.trading_name.trim()) { setFormError('Trading name is required'); return; }
    if (!form.start_date) { setFormError('Start date is required'); return; }
    if (form.end_date && form.end_date < form.start_date) {
      setFormError('End date cannot be before start date');
      return;
    }
    try {
      setSaving(true);
      setFormError(null);
      const payload = {
        pocket_id: form.pocket_id,
        trading_name: form.trading_name.trim(),
        registered_name: form.registered_name.trim() || undefined,
        account_number: form.account_number.trim() || undefined,
        start_date: form.start_date,
        end_date: form.end_date || null,
      };
      if (editingTenant) {
        await api.updateTenant(editingTenant.id, {
          trading_name: payload.trading_name,
          registered_name: payload.registered_name ?? null,
          account_number: payload.account_number ?? null,
          start_date: payload.start_date,
          end_date: payload.end_date,
        });
      } else {
        await api.createTenant(payload);
      }
      setShowModal(false);
      await loadData();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save tenant');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t: PocketTenant) => {
    if (!confirm(`Delete tenant "${t.trading_name}" (${t.start_date} → ${t.end_date ?? 'open'})? This is irreversible.`)) return;
    try {
      await api.deleteTenant(t.id);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete tenant');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-content">Tenants</h1>
          <p className="text-content-muted mt-1">Pocket occupants. Status is derived from start/end dates.</p>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-accent text-accent-fg rounded-lg hover:opacity-90 transition-colors flex items-center gap-2"
        >
          + Add Tenant
        </button>
      </div>

      {error && <Alert tone="danger" className="mb-4">{error}</Alert>}

      {/* Filters */}
      <Card className="p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-content-muted mb-1">Search</label>
          <input
            type="text"
            placeholder="Trading name, account number, pocket…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-content-muted mb-1">Building</label>
          <select
            value={buildingFilter}
            onChange={e => { setBuildingFilter(e.target.value); setPocketFilter(''); }}
            className="px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All buildings</option>
            {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-content-muted mb-1">Pocket</label>
          <select
            value={pocketFilter}
            onChange={e => setPocketFilter(e.target.value)}
            className="px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All pockets</option>
            {pockets
              .filter(p => !buildingFilter || p.building_asset_id === buildingFilter)
              .map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-content-muted mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as StatusFilter)}
            className="px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="active">Active</option>
            <option value="past">Past</option>
            <option value="all">All</option>
          </select>
        </div>
      </Card>

      {/* Table */}
      {/* overflow-hidden stays on Card (clips the table's square corners to
          the card's rounded ones); DataTable now owns the actual horizontal
          scroller (its own `overflow-x-auto` internal div) — the Phase 0
          scroller this file used to hand-roll here for that same reason. */}
      <Card className="overflow-hidden">
        <DataTable
          data={filtered}
          columns={tenantColumns}
          rowKey={(t) => t.id}
          emptyState={
            <p>
              {tenants.length === 0
                ? 'No tenants yet. Click "+ Add Tenant" to get started.'
                : 'No tenants match the filters.'}
            </p>
          }
          /* `lg:min-h-0` on both row actions: `.tap-target`'s 44px floor is a
             TOUCH floor, and inside a table row the button becomes the row's
             height driver — a `comfortable` row (px-5 py-4 = 32px of padding
             + ~20px of line) is ~52px, so an unconditional 44px button pushes
             it to ~76px and costs ~30% of the rows-per-screen an admin can
             scan (spec §3 D3 / §8.6). The escape is the one documented at
             tailwind.config.js:103; it works because `.tap-target` is
             registered in the `components` layer, which Tailwind emits before
             `utilities`, so an equal-specificity utility wins on source order
             (verified against the emitted dist CSS, not assumed). min-WIDTH is
             deliberately left at 44px — it costs nothing vertically.
             Page-level CTAs (e.g. BillRunDetail's approve/reject/finalize) do
             NOT get this: 44px is right for a primary button on desktop. */
          rowActions={(t) => (
            <>
              <button
                onClick={() => openEdit(t)}
                className="tap-target lg:min-h-0 text-accent hover:opacity-90 text-sm font-medium mr-3"
              >
                Edit
              </button>
              {/* text-red-600 is 3.14:1 against the Card's dark surface
                  (#292524) — fails AA. dark:text-red-400 (5.49:1, computed by
                  hand) passes; dark:hover:text-red-300 lightens further on
                  hover, consistent with the existing light-mode
                  hover:text-red-800 darkening-on-hover convention. */}
              <button
                onClick={() => handleDelete(t)}
                className="tap-target lg:min-h-0 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium"
              >
                Delete
              </button>
            </>
          )}
        />
      </Card>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          {/* Plain bg-surface-raised div, not <Card> — matches the modal
              precedent (fixed inset-0 overlays keep their own rounded-xl/
              shadow-2xl rather than inheriting Card's rounded-lg). */}
          <div className="bg-surface-raised rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-content mb-4">
              {editingTenant ? 'Edit Tenant' : 'Add Tenant'}
            </h2>
            {formError && (
              <Alert tone="danger" className="mb-4">
                {formError}
              </Alert>
            )}
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                {/* text-red-600 required-marker is 3.14:1 against
                    surface-raised dark — fails AA. dark:text-red-400
                    (5.49:1, computed by hand) passes. Same fix applied to
                    every "*" marker below. */}
                <label className="block text-sm font-medium text-content mb-1">
                  Pocket <span className="text-red-600 dark:text-red-400">*</span>
                </label>
                <select
                  required
                  value={form.pocket_id}
                  onChange={e => setForm(p => ({ ...p, pocket_id: e.target.value }))}
                  disabled={!!editingTenant}
                  className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-surface-sunken disabled:text-content-muted"
                >
                  <option value="">Select a pocket…</option>
                  {pockets.map(p => {
                    const b = p.building_asset_id ? buildingNameById.get(p.building_asset_id) : null;
                    return (
                      <option key={p.id} value={p.id}>
                        {p.name}{b ? ` — ${b}` : ''}
                      </option>
                    );
                  })}
                </select>
                {editingTenant && (
                  <p className="text-xs text-content-muted mt-1">
                    To move a tenant to a different pocket, delete this and create a new one.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-content mb-1">
                  Trading Name <span className="text-red-600 dark:text-red-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.trading_name}
                  onChange={e => setForm(p => ({ ...p, trading_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-content mb-1">Registered Name</label>
                <input
                  type="text"
                  value={form.registered_name}
                  onChange={e => setForm(p => ({ ...p, registered_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-content mb-1">Account Number</label>
                <input
                  type="text"
                  value={form.account_number}
                  onChange={e => setForm(p => ({ ...p, account_number: e.target.value }))}
                  className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 font-mono"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-content mb-1">
                    Start Date <span className="text-red-600 dark:text-red-400">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={form.start_date}
                    onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-xs text-content-muted mt-1">First day of beneficial occupation</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-content mb-1">End Date</label>
                  <input
                    type="date"
                    value={form.end_date}
                    onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-xs text-content-muted mt-1">Last occupied day; leave blank for open-ended</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-content bg-surface-sunken rounded-lg hover:bg-border-token"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-accent text-accent-fg rounded-lg hover:opacity-90 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : (editingTenant ? 'Save changes' : 'Add Tenant')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
